import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Layers, Users, Shirt, Palette, FolderOpen } from "lucide-react";

const BRANDS = ["MAISON NOVA", "ATELIER NEUF", "HOUSE OF LINEA", "VERRE & CO", "STUDIO KIN", "ORO APPAREL"];

const FEATURES = [
  { icon: Sparkles, title: "Sketch to realistic render", desc: "Visualize your product ideas 30x faster. Upload a sketch and get a photorealistic render in seconds.", span: "md:col-span-2" },
  { icon: Palette, title: "Materials, prints & colorways", desc: "Apply fabrics, prints and colorways from curated libraries to any garment instantly." },
  { icon: Shirt, title: "Virtual model try-on", desc: "See your outfit on diverse models, in any setting or location — no photoshoot needed." },
  { icon: Layers, title: "Iteration history", desc: "Every version preserved. Compare, branch and refine without losing a single idea.", span: "md:col-span-2" },
  { icon: FolderOpen, title: "Collections & lookbooks", desc: "Curate approved designs into collections and export to PDF or PowerPoint." },
  { icon: Users, title: "Team collaboration", desc: "Shared workspace with comments so buying and design teams stay aligned." },
];

const STATS = [
  { before: "45 min", after: "15 min", label: "Turn a sketch into a realistic render", boost: "3x Faster" },
  { before: "2 hours", after: "15 min", label: "Apply materials to a garment", boost: "8x Faster" },
  { before: "2 days", after: "15 min", label: "Visualize an outfit on a model", boost: "192x Faster" },
];

export default function Landing() {
  return (
    <div className="bg-white text-zinc-950 min-h-screen">
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-heading font-bold text-xl tracking-tight" data-testid="landing-logo">LUMINA<span className="text-zinc-400">.AI</span></span>
          <div className="flex items-center gap-3">
            <Link to="/auth" data-testid="landing-login-link" className="text-sm font-medium hover:text-zinc-500 transition-colors px-4 py-2">Log in</Link>
            <Link to="/auth?mode=register" data-testid="landing-get-started-btn" className="text-sm font-medium bg-zinc-950 text-white px-5 py-2.5 rounded-md hover:bg-zinc-800 transition-colors">Get started</Link>
          </div>
        </div>
      </nav>

      <section className="pt-36 pb-20 px-6 max-w-7xl mx-auto">
        <div className="max-w-3xl fade-up">
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6">Generative AI for Apparel, Accessories & Shoes</p>
          <h1 className="font-heading text-5xl sm:text-6xl font-bold leading-[1.05] tracking-tight">
            AI made for your<br />fashion brand.
          </h1>
          <p className="mt-6 text-lg text-zinc-600 max-w-xl leading-relaxed">
            Turn sketches into photorealistic renders, apply materials and prints from libraries, and fit garments on virtual models — before anything is physically made.
          </p>
          <div className="mt-10 flex items-center gap-4">
            <Link to="/auth?mode=register" data-testid="hero-cta-btn" className="inline-flex items-center gap-2 bg-zinc-950 text-white px-7 py-3.5 rounded-md font-medium hover:bg-zinc-800 transition-colors">
              Start designing <ArrowRight size={16} />
            </Link>
            <span className="text-sm text-zinc-400">Reduce sampling. Ship faster.</span>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200 rounded-lg overflow-hidden">
          <div className="bg-zinc-50 p-4 relative">
            <span className="absolute top-6 left-6 z-10 text-[10px] uppercase tracking-[0.2em] bg-white/90 px-3 py-1.5 rounded">01 — The Sketch</span>
            <img src={`${process.env.PUBLIC_URL}/assets/hero/sketch-hero.jpg`} alt="Fashion sketch" className="w-full h-[420px] object-cover rounded" />
          </div>
          <div className="bg-zinc-50 p-4 relative">
            <span className="absolute top-6 left-6 z-10 text-[10px] uppercase tracking-[0.2em] bg-zinc-950 text-white px-3 py-1.5 rounded">02 — The Render</span>
            <img src={`${process.env.PUBLIC_URL}/assets/hero/render-hero.jpg`} alt="Photorealistic render" className="w-full h-[420px] object-cover rounded" />
          </div>
        </div>
      </section>

      <section className="py-10 border-y border-zinc-100 overflow-hidden">
        <div className="flex whitespace-nowrap marquee">
          {[...BRANDS, ...BRANDS].map((b, i) => (
            <span key={i} className="mx-10 text-sm font-heading tracking-[0.3em] text-zinc-400">{b}</span>
          ))}
        </div>
      </section>

      <section className="py-24 px-6 max-w-7xl mx-auto">
        <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">One spot for creative teams</p>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">Design an entire collection with ease, all in one place.</h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className={`border border-zinc-200 rounded-lg p-8 hover:shadow-sm hover:-translate-y-1 transition-transform duration-200 bg-white ${f.span || ""}`}>
              <f.icon size={22} className="mb-5" />
              <h3 className="font-heading font-semibold text-lg">{f.title}</h3>
              <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 px-6 bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-4">Save time, design faster</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Cut 75–80% off your core design tasks.</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800 rounded-lg overflow-hidden">
            {STATS.map((s) => (
              <div key={s.label} className="bg-zinc-950 p-8">
                <div className="flex items-baseline gap-3">
                  <span className="text-zinc-500 line-through text-lg">{s.before}</span>
                  <span className="font-heading text-4xl font-bold">{s.after}</span>
                </div>
                <p className="mt-4 text-sm text-zinc-400">{s.label}</p>
                <span className="mt-4 inline-block text-xs uppercase tracking-[0.2em] border border-zinc-700 px-3 py-1.5 rounded">{s.boost}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28 px-6 text-center">
        <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Ready to start?</h2>
        <p className="mt-4 text-zinc-500">Design your next drop before the deadline hits.</p>
        <Link to="/auth?mode=register" data-testid="footer-cta-btn" className="mt-8 inline-flex items-center gap-2 bg-zinc-950 text-white px-8 py-4 rounded-md font-medium hover:bg-zinc-800 transition-colors">
          Start designing <ArrowRight size={16} />
        </Link>
      </section>

      <footer className="border-t border-zinc-100 py-8 px-6 text-center text-xs text-zinc-400 tracking-wide">
        LUMINA.AI — Enterprise generative AI for fashion design teams
      </footer>
    </div>
  );
}
